<template>
  <Modal v-if="showModal" :name="modalName" custom @before-close="closeTooltips">
    <div class="announcement-modal">
      <CloseIcon class="announcement-modal__close-icon" @click="handleClose" />

      <Component
        :is="feature.component"
        v-for="feature in visibleFeatures"
        v-show="currentFeature.key === feature.key"
        :key="feature.key"
        @close="handleClose"
      />

      <div v-if="visibleFeatures.length > 1" class="announcement-modal__carousel">
        <div class="announcement-modal__prev-button" @click="goToPrevious">
          <ChevronIcon height="12" width="12" />
        </div>
        <div class="announcement-modal__next-button" @click="goToNext">
          <ChevronIcon height="12" width="12" />
        </div>
      </div>
    </div>
  </Modal>
</template>
<script lang="ts">
import Vue from 'vue';
import PublicPortfolioAnnouncement from '~/components/partials/public-portfolio/PublicPortfolioAnnouncement/PublicPortfolioAnnouncement.vue';
import { ONBOARDING_TOOLTIP_NAME } from '~/components/partials/war-room-onboarding/constants';
import type { IAnnouncementFeature } from '~/components/common/AnnouncementModal/constants';
import { ANNOUNCEMENT_FEATURES } from '~/components/common/AnnouncementModal/constants';
import Modal from '~/components/common/Modal/Modal.vue';
import ChevronIcon from '~/assets/svg/chevron-left.vue.svg';
import CloseIcon from '~/assets/svg/close.vue.svg';

export default Vue.extend({
  name: 'AnnoucementModal',
  components: {
    PublicPortfolioAnnouncement,
    Modal,
    ChevronIcon,
    CloseIcon,
  },
  data(): { modalName: string; currentIndex: number } {
    return {
      modalName: this.$constants.dynamicModals.WAR_ROOM_ANNOUNCEMENT_MODAL,
      currentIndex: 0,
    };
  },
  computed: {
    visibleFeatures(): Array<IAnnouncementFeature> {
      const items: IAnnouncementFeature[] = ANNOUNCEMENT_FEATURES;

      if (!this.$auth.loggedIn || !this.$auth.user.admin) {
        return items.filter(item => !item.onlyForAdmins);
      }

      return items;
    },
    currentFeature(): IAnnouncementFeature | null {
      return this.showModal ? this.visibleFeatures[this.currentIndex] : null;
    },
    showModal(): boolean {
      return (
        !!this.$tooltip.isPresent(ONBOARDING_TOOLTIP_NAME) &&
        !!this.visibleFeatures.length &&
        this.visibleFeatures.some(feature => !this.$tooltip.isPresent(feature.tooltipName)) &&
        this.currentIndex > -1 &&
        this.currentIndex <= this.visibleFeatures.length - 1
      );
    },
  },
  mounted() {
    if (!this.$auth.loggedIn) return;

    this.handleModalVisibility();
  },
  methods: {
    handleModalVisibility(): void {
      if (
        this.$tooltip.isPresent(ONBOARDING_TOOLTIP_NAME) &&
        this.visibleFeatures.some(feature => !this.$tooltip.isPresent(feature.tooltipName)) &&
        this.$route.query.dynamic_modal !== this.modalName
      ) {
        this.$modal.showWithRoute({ custom: this.modalName });
      }
    },
    closeTooltips(): void {
      this.visibleFeatures.forEach(feature => this.$tooltip.close(feature.tooltipName));
    },
    handleClose(): void {
      this.$modal.hide(this.modalName);
    },
    goToNext(): void {
      if (this.currentIndex < this.visibleFeatures.length - 1) {
        this.currentIndex++;
      } else {
        this.currentIndex = 0;
      }
    },
    goToPrevious(): void {
      if (this.currentIndex > 0) {
        this.currentIndex--;
      } else {
        this.currentIndex = this.visibleFeatures.length - 1;
      }
    },
  },
});
</script>

<style scoped lang="scss">
.announcement-modal {
  display: flex;
  flex-direction: column;
  position: relative;
  margin-top: 40px;
  align-self: flex-start;

  @include mq('sm') {
    margin-top: 70px;
  }

  &__close-icon {
    width: 24px;
    height: 24px;
    color: var(--white);
    position: absolute;
    cursor: pointer;
    top: 16px;
    left: 18px;
    opacity: 0.5;

    &:hover {
      opacity: 1;
    }
  }

  &__carousel {
    display: flex;
    justify-content: center;
  }

  &__prev-button,
  &__next-button {
    color: var(--white);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #797b86;
    opacity: 0.5;
    z-index: 3;
    margin: 16px 5px 0;
    cursor: pointer;
  }

  &__next-button {
    svg {
      transform: scaleX(-1);
    }
  }
}
</style>
